import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Eye, EyeOff, Ticket, ShieldCheck, Stethoscope, Pencil } from 'lucide-react';
import PracticeDetailsModal from '../components/PracticeDetailsModal';

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [practiceModalOpen, setPracticeModalOpen] = useState(false);
  const [practiceProfile, setPracticeProfile] = useState(null);
  const { signup } = useAuth();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();

  const isBeta = flags.enable_invite_only_beta;
  const betaSpecialty = flags.beta_specialty_id || '';

  const validatePassword = (pwd) => ({
    length: pwd.length >= 8,
    uppercase: /[A-Z]/.test(pwd),
    lowercase: /[a-z]/.test(pwd),
    number: /\d/.test(pwd),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
  });

  const passwordChecks = validatePassword(password);
  const allChecksPassed = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (!allChecksPassed) { toast.error('Password does not meet requirements'); return; }
    if (isBeta && !inviteCode.trim()) { toast.error('Please enter your invite code'); return; }

    setLoading(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await signup(email, password, fullName, timezone, inviteCode.trim() || undefined, practiceProfile || undefined);
      setSuccess(true);
      toast.success('Account created! You can sign in now.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error_code) {
        switch (detail.error_code) {
          case 'invite_required':
            toast.error('An invite code is required to join the beta.'); break;
          case 'invalid_invite':
            toast.error('This invite code is invalid or has already been used.'); break;
          default:
            toast.error(detail.message || 'Signup failed.');
        }
      } else if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'Signup failed');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePracticeSave = (profile) => {
    setPracticeProfile(profile);
    if (profile) toast.success('Practice details saved');
  };

  // How many practice fields filled?
  const practiceFieldCount = practiceProfile
    ? Object.values(practiceProfile).filter(v => Array.isArray(v) ? v.length > 0 : !!v).length
    : 0;

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md" data-testid="signup-success-card">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6 text-sky-600" />
            </div>
            <CardTitle className="text-2xl">Account Created</CardTitle>
            <CardDescription>
              Your LitCurrent account for <strong>{email}</strong> is ready.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={() => navigate('/login')}
              className="w-full"
              data-testid="go-login-btn"
            >
              Continue to Login
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Email verification is not required before app access in this environment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 mb-1">
            <CardTitle className="text-2xl">Create Your Account</CardTitle>
            {isBeta && <Badge className="bg-blue-100 text-blue-700 text-xs">Beta</Badge>}
          </div>
          <CardDescription>
            {isBeta
              ? 'LitCurrent is in invite-only beta. Enter your invite code to join.'
              : 'Join LitCurrent to get personalized literature digests'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Invite Code */}
            {isBeta && (
              <div className="space-y-2">
                <Label htmlFor="inviteCode" className="flex items-center gap-1.5">
                  <Ticket className="h-3.5 w-3.5 text-blue-600" />
                  Invite Code *
                </Label>
                <Input id="inviteCode" type="text" placeholder="Enter your invite code" value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)} required={isBeta}
                  data-testid="invite-code-input" className="font-mono tracking-wider" />
                <p className="text-xs text-gray-500">Received from your LitCurrent contact or email invitation.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input id="fullName" type="text" placeholder="Dr. Jane Smith" value={fullName}
                onChange={(e) => setFullName(e.target.value)} data-testid="fullname-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" placeholder="you@example.com" value={email}
                onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} required data-testid="password-input" className="pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700" tabIndex={-1}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {password && (
                <div className="mt-2 space-y-1 text-xs">
                  {[['length','At least 8 characters'],['uppercase','One uppercase letter'],['lowercase','One lowercase letter'],['number','One number'],['special','One special character']].map(([key,label]) => (
                    <div key={key} className="flex items-center gap-2">
                      {passwordChecks[key] ? <CheckCircle2 className="h-3 w-3 text-green-600" /> : <XCircle className="h-3 w-3 text-gray-300" />}
                      <span className={passwordChecks[key] ? 'text-green-600' : 'text-gray-500'}>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <div className="relative">
                <Input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="confirm-password-input" className="pr-10" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700" tabIndex={-1}>
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && <p className="text-xs text-red-600">Passwords do not match</p>}
            </div>

            {/* ── Optional Practice Details Card ── */}
            <div className="border border-gray-200 rounded-lg p-3.5 bg-gray-50/50" data-testid="practice-details-section">
              {practiceProfile ? (
                /* Confirmation state: details saved */
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Practice details added</p>
                      <p className="text-xs text-gray-500">{practiceFieldCount} field{practiceFieldCount !== 1 ? 's' : ''} completed</p>
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setPracticeModalOpen(true)}
                    data-testid="practice-edit-btn" className="text-blue-600 hover:text-blue-700">
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                </div>
              ) : (
                /* Opt-in state: collapsed */
                <div className="space-y-2">
                  <div className="flex items-start gap-2.5">
                    <Stethoscope className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700">Optional: Help us tailor LitCurrent to your practice</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Share a few details about your practice setting so we can customize your experience. All questions are optional.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => setPracticeModalOpen(true)}
                      data-testid="practice-add-btn"
                      className="text-xs">
                      <Stethoscope className="h-3.5 w-3.5 mr-1.5" /> Add practice details
                    </Button>
                    <span className="text-xs text-gray-400">You can update this later</span>
                  </div>
                </div>
              )}
            </div>

            <Button type="submit" className="w-full"
              disabled={loading || !allChecksPassed || password !== confirmPassword || (isBeta && !inviteCode.trim())}
              data-testid="signup-button">
              {loading ? 'Creating account...' : isBeta ? 'Join Beta' : 'Create Account'}
            </Button>

            {isBeta && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs font-medium text-blue-800">
                  <ShieldCheck className="h-3.5 w-3.5" /> Beta includes:
                </div>
                <ul className="text-xs text-blue-700 space-y-0.5 ml-5 list-disc">
                  <li>30-day full Pro access</li>
                  <li>AI-powered literature digests</li>
                  <li>Audio article summaries</li>
                  <li>Deep Dive with LitScholar AI</li>
                  <li>Specialty community access</li>
                </ul>
              </div>
            )}
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-gray-600">Already have an account? </span>
            <Link to="/login" className="text-blue-600 hover:underline font-medium">Sign in</Link>
          </div>
        </CardContent>
      </Card>

      {/* Practice Details Modal */}
      <PracticeDetailsModal
        open={practiceModalOpen}
        onOpenChange={setPracticeModalOpen}
        profile={practiceProfile}
        onSave={handlePracticeSave}
        betaSpecialty={betaSpecialty}
      />
    </div>
  );
};

export default SignupPage;
